
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface TestCallVariablesProps {
  variables: Record<string, string>;
  onChange: (variables: Record<string, string>) => void;
}

// These variables are required for the test call to work properly with ElevenLabs
const REQUIRED_VARIABLES = [
  "lead_first_name",
  "agent_name",
  "dealership_name"
];

const TestCallVariables = ({ variables, onChange }: TestCallVariablesProps) => {
  const [newVarKey, setNewVarKey] = useState("");
  const [newVarValue, setNewVarValue] = useState("");

  // Initialize required variables if they don't exist
  useEffect(() => {
    const updatedVariables = { ...variables };
    let hasChanges = false;

    REQUIRED_VARIABLES.forEach(key => {
      if (updatedVariables[key] === undefined) {
        updatedVariables[key] = "";
        hasChanges = true;
      }
    });

    if (hasChanges) {
      onChange(updatedVariables);
    }
  }, []);

  const handleAddVariable = () => {
    if (!newVarKey.trim()) return;
    
    onChange({
      ...variables,
      [newVarKey]: newVarValue
    });
    
    setNewVarKey("");
    setNewVarValue("");
    
    toast({
      title: "Variable added",
      description: `Added ${newVarKey} to variables`
    });
  };

  const handleRemoveVariable = (key: string) => {
    // Don't allow removing required variables
    if (REQUIRED_VARIABLES.includes(key)) {
      toast({
        title: "Cannot remove required variable",
        description: `${key} is required for the call to work properly`,
        variant: "destructive"
      });
      return;
    }
    
    const newVariables = { ...variables };
    delete newVariables[key];
    onChange(newVariables);
  };

  const handleValueChange = (key: string, value: string) => {
    onChange({
      ...variables,
      [key]: value
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center mb-2">
          <Label className="text-sm font-medium flex-grow">Required Variables</Label>
          <span className="text-xs text-muted-foreground">All fields must be filled</span>
        </div>
        {REQUIRED_VARIABLES.map((key) => (
          <div key={key} className="flex items-center gap-2">
            <div className="flex-shrink-0 w-1/3">
              <Input 
                value={key} 
                disabled 
                className="bg-muted"
              />
            </div>
            <div className="flex-grow">
              <Input 
                value={variables[key] || ""} 
                onChange={e => handleValueChange(key, e.target.value)}
                placeholder={`Enter ${key.replace(/_/g, ' ')}`}
              />
            </div>
          </div>
        ))}
      </div>

      {Object.entries(variables)
        .filter(([key]) => !REQUIRED_VARIABLES.includes(key))
        .length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Additional Variables</Label>
          {Object.entries(variables)
            .filter(([key]) => !REQUIRED_VARIABLES.includes(key))
            .map(([key, value]) => (
            <div key={key} className="flex items-center gap-2">
              <div className="flex-shrink-0 w-1/3">
                <Input 
                  value={key} 
                  disabled 
                  className="bg-muted"
                />
              </div>
              <div className="flex-grow">
                <Input 
                  value={value} 
                  onChange={e => handleValueChange(key, e.target.value)}
                  placeholder={`Value for ${key}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveVariable(key)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-grow">
          <Label htmlFor="varKey" className="text-xs">New Variable Name</Label>
          <Input
            id="varKey"
            value={newVarKey}
            onChange={e => setNewVarKey(e.target.value)}
            placeholder="e.g., vehicle_model"
          />
        </div>
        <div className="flex-grow">
          <Label htmlFor="varValue" className="text-xs">Value</Label>
          <Input
            id="varValue"
            value={newVarValue}
            onChange={e => setNewVarValue(e.target.value)}
            placeholder="e.g., Tesla Model 3"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleAddVariable}
          disabled={!newVarKey.trim()}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default TestCallVariables;
